/**
 * El cliente de WebSocket de la partida.
 *
 * Tres decisiones que conviene entender antes de tocarlo:
 *
 * - **El token va en el primer mensaje, no en la URL.** Los parámetros de consulta acaban en los
 *   registros del proxy y en el historial del navegador; un token de acceso ahí es un token
 *   filtrado.
 * - **Hay códigos de cierre que no se reintentan.** Insistir tras «tu cuenta se ha abierto en otro
 *   sitio» produce dos clientes peleándose por la sesión, cada uno expulsando al otro para siempre.
 * - **La reconexión es la norma, no la excepción.** En un aula la wifi se cae; el estado de la
 *   conexión se muestra siempre, y volver es automático.
 */

import {
  CLOSE_CODES,
  FINAL_CLOSE_CODES,
  type ClientMessage,
  type ServerMessage,
} from '@clica/shared';
import { backoffDelay } from './backoff.js';

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

/** Lo mínimo que el cliente usa de un `WebSocket`, para poder sustituirlo en las pruebas. */
export interface SocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: unknown) => void) | null;
  onclose: ((event: { code: number }) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export interface GameSocketOptions {
  readonly url: string;
  /** Se pide en cada intento: entre dos reintentos el token puede haberse refrescado. */
  readonly token: () => Promise<string | null>;
  readonly deviceId: string;
  readonly pin?: string;
  readonly onMessage: (message: ServerMessage) => void;
  readonly onStatus: (status: ConnectionStatus, closeCode: number | null) => void;
  readonly createSocket?: (url: string) => SocketLike;
  readonly random?: () => number;
}

export class GameSocket {
  private socket: SocketLike | null = null;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  /** Lo que se intentó enviar mientras no había conexión. Se manda al abrir. */
  private readonly pending: ClientMessage[] = [];

  constructor(private readonly options: GameSocketOptions) {}

  connect(): void {
    this.stopped = false;
    this.open(this.attempt === 0 ? 'connecting' : 'reconnecting');
  }

  /** Cierre a petición del usuario: no reintenta. */
  close(): void {
    this.stopped = true;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.socket?.close(1000, 'cierre del cliente');
    this.socket = null;
    this.options.onStatus('closed', null);
  }

  /**
   * Envía un mensaje, o lo guarda si la conexión no está lista.
   *
   * Guardarlo importa: el alumno que pulsa «entrar» justo cuando se le cae la wifi vería su acción
   * evaporarse, y volvería a pulsar sin saber si la primera vez contó.
   */
  send(message: ClientMessage): void {
    if (this.socket === null) {
      this.pending.push(message);
      return;
    }
    this.socket.send(JSON.stringify(message));
  }

  private open(status: ConnectionStatus): void {
    this.options.onStatus(status, null);

    const create =
      this.options.createSocket ?? ((url) => new WebSocket(url) as unknown as SocketLike);
    const socket = create(this.options.url);
    this.socket = socket;

    socket.onopen = () => {
      void this.greet(socket);
    };

    socket.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }
      this.options.onMessage(parsed as ServerMessage);
    };

    socket.onclose = (event) => {
      this.socket = null;
      if (this.stopped) return;

      // Insistir con estos no arregla nada, y con `sessionReplaced` produce dos clientes
      // expulsándose mutuamente sin fin.
      if ((FINAL_CLOSE_CODES as readonly number[]).includes(event.code)) {
        this.options.onStatus('closed', event.code);
        return;
      }

      this.scheduleRetry(event.code);
    };

    socket.onerror = () => {
      // No se hace nada aquí a propósito: tras un error siempre llega el cierre, y reaccionar en
      // los dos sitios programaría dos reintentos por cada caída.
    };
  }

  private async greet(socket: SocketLike): Promise<void> {
    const token = await this.options.token();

    if (token === null) {
      // Sin token no hay saludo posible. Cerrar con el código del servidor deja al resto del
      // cliente en el mismo camino que un token rechazado, en lugar de en un estado propio.
      socket.close(CLOSE_CODES.badToken, 'sin sesión');
      return;
    }

    // El socket puede haberse caído mientras se pedía el token.
    if (this.socket !== socket) return;

    socket.send(
      JSON.stringify({
        type: 'hello',
        token,
        deviceId: this.options.deviceId,
        ...(this.options.pin === undefined ? {} : { pin: this.options.pin }),
      } satisfies ClientMessage),
    );

    this.attempt = 0;
    this.options.onStatus('open', null);

    for (const message of this.pending.splice(0)) {
      socket.send(JSON.stringify(message));
    }
  }

  private scheduleRetry(closeCode: number): void {
    const delay = backoffDelay(this.attempt, this.options.random);
    this.attempt += 1;
    this.options.onStatus('reconnecting', closeCode);

    this.timer = setTimeout(() => {
      this.timer = null;
      if (!this.stopped) this.open('reconnecting');
    }, delay);
  }
}
